import { useMemo, useState, type FormEvent } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { IconCheck } from '@tabler/icons-react'
import {
  brownActWarning,
  createAnnouncement,
  createMeeting,
  MEETING_TYPES,
  TenantAdminApiError,
  type AdminMeeting,
} from '@/lib/tenantAdminApi'
import { formatDay } from '@/lib/dateOnly'
import { useTenantAdmin } from '@/pages/tenant-admin/context'

/**
 * Screen 3 — post a meeting (ag-008 wireframes).
 *
 * Links-only MVP: the GSA pastes links to its agenda/minutes; video is always
 * link-only by design (we link, we don't host). Posting publishes directly to
 * the public page — the GSA is the authority for its own documents. The
 * Brown-Act lead-time check is a non-blocking reminder, never an enforcement.
 */
export default function TenantAdminMeetingNew() {
  const { slug, displayName } = useTenantAdmin()
  const navigate = useNavigate()

  const [meetingDate, setMeetingDate] = useState('')
  const [meetingType, setMeetingType] = useState<string>('Regular')
  const [agendaUrl, setAgendaUrl] = useState('')
  const [minutesUrl, setMinutesUrl] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [location, setLocation] = useState('')
  const [announcementText, setAnnouncementText] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [posted, setPosted] = useState<AdminMeeting | null>(null)
  const [announcementFailed, setAnnouncementFailed] = useState(false)

  // Live, client-side Brown-Act reminder (mirrors the server check).
  const liveWarning = useMemo(
    () => (meetingDate ? brownActWarning(meetingType, meetingDate) : null),
    [meetingType, meetingDate],
  )

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    setAnnouncementFailed(false)

    createMeeting(slug, {
      meeting_date: meetingDate,
      meeting_type: meetingType,
      agenda_url: agendaUrl.trim() || null,
      minutes_url: minutesUrl.trim() || null,
      video_url: videoUrl.trim() || null,
      location: location.trim() || null,
    })
      .then(async (meeting) => {
        // Optional companion announcement — published immediately alongside
        // the meeting (best-effort: a failure here never unwinds the meeting).
        const text = announcementText.trim()
        if (text) {
          try {
            await createAnnouncement(slug, {
              title: `${meetingType} meeting — ${formatDay(meeting.meeting_date) || meetingDate}`,
              body: text,
              publish: true,
            })
          } catch {
            setAnnouncementFailed(true)
          }
        }
        setPosted(meeting)
      })
      .catch((err) => {
        if (err instanceof TenantAdminApiError && err.status === 401) {
          navigate(`/g/${slug}/admin/login`, { replace: true })
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to post the meeting.')
      })
      .finally(() => setSubmitting(false))
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (posted) {
    return (
      <Card sx={{ maxWidth: 640 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <IconCheck size={22} color="green" />
            <Typography variant="h5">Meeting posted</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {formatDay(posted.meeting_date)}
            {posted.meeting_type ? ` · ${posted.meeting_type}` : ''} is now live on your public
            page, posted by {displayName}.
          </Typography>
          {!posted.brown_act.ok && posted.brown_act.note ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Brown Act reminder: {posted.brown_act.note}
            </Alert>
          ) : null}
          {announcementFailed ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The meeting was posted, but the companion announcement failed — you can add it on
              the Announcements screen.
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button component={RouterLink} to={`/g/${slug}`} variant="contained">
              View public page
            </Button>
            <Button component={RouterLink} to={`/g/${slug}/admin`} variant="outlined">
              Back to dashboard
            </Button>
            <Button
              onClick={() => {
                setPosted(null)
                setMeetingDate('')
                setAgendaUrl('')
                setMinutesUrl('')
                setVideoUrl('')
                setLocation('')
                setAnnouncementText('')
              }}
            >
              Post another
            </Button>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Post a meeting
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Publishes directly to your public page, badged “Posted by {displayName}”.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <form onSubmit={onSubmit}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Meeting date"
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  required
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label="Type"
                  select
                  value={meetingType}
                  onChange={(e) => setMeetingType(e.target.value)}
                  required
                  fullWidth
                >
                  {MEETING_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {liveWarning ? <Alert severity="warning">{liveWarning}</Alert> : null}

              <TextField
                label="Agenda link"
                type="url"
                value={agendaUrl}
                onChange={(e) => setAgendaUrl(e.target.value)}
                fullWidth
                placeholder="https://…/agenda.pdf"
                helperText="Paste a link to the agenda PDF (file upload is coming later)."
              />
              <TextField
                label="Minutes link (optional)"
                type="url"
                value={minutesUrl}
                onChange={(e) => setMinutesUrl(e.target.value)}
                fullWidth
                placeholder="https://…/minutes.pdf"
              />
              <TextField
                label="Video link (optional)"
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                fullWidth
                placeholder="https://youtube.com/…"
                helperText="YouTube/Vimeo link only — we link to your video, we don’t host it."
              />
              <TextField
                label="Location (optional)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
              />
              <TextField
                label="Announcement (optional)"
                value={announcementText}
                onChange={(e) => setAnnouncementText(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                helperText="Published immediately as an announcement alongside the meeting."
              />

              <Stack direction="row" spacing={1.5}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? 'Posting…' : 'Post meeting'}
                </Button>
                <Button component={RouterLink} to={`/g/${slug}/admin`} disabled={submitting}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  )
}
